import {
  SHARE_ORIGIN,
  SHARE_PATH_PATTERNS,
  shareLinkSource,
} from "@fazly/shared/shareLinks";

/**
 * A share URL with the code taken out: `/r/AbCd12` → `/r/:code`.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * `/r/<code>`, `/p/<code>` and `/scan/<id>` carry one person's referral code,
 * one community post's share code, or one person's scan id in the PATH, and
 * the pages behind them deliberately refuse to resolve any of it
 * (`ShareLanding`). `lib/analytics.ts` states the rule that follows: events
 * carry the KIND of thing shared, never which one. Our own `track` calls kept
 * it. The SDK did not: posthog-js stamps the page's URL onto EVERY event it
 * sends — `$current_url`, `$pathname`, the session's entry URL, the previous
 * pageview's path — and `lib/vitals.ts` added `path` on top. So until the
 * 2026-10-27 security audit (h8-web-2) the code reached PostHog on every
 * pageview, pageleave and vitals row of a share landing, in exactly the store
 * the landing event's own comment promised it would never reach.
 *
 * ── What is kept ─────────────────────────────────────────────────────────────
 *
 * The kind (the prefix) and the `?s=` surface that produced the link, which is
 * what every funnel built on these pages breaks down by — and only a `?s=`
 * that `shareLinkSource` would accept, the same bounded alphabet the landing
 * event already sends, so the URL property cannot carry a value the event
 * property refuses. Every other query parameter and the fragment are dropped:
 * nothing in the app writes them onto a share link, and a link anybody can
 * construct can carry anything in them.
 *
 * ── Why derived from SHARE_PATH_PATTERNS ─────────────────────────────────────
 *
 * The app, the association files and the deep-link router already read their
 * share paths from that one table in `packages/shared`. A fourth share path
 * added there must be scrubbed here without anybody remembering this file, so
 * the prefixes are read from it; `sharePaths.test.ts` fails if a new prefix
 * has no placeholder of its own (it would get `:code`, which is still safe).
 */

/** What a share path's segment becomes in analytics and in the crawler log. */
export const SHARE_PLACEHOLDERS: Readonly<Record<string, string>> = {
  r: ":code",
  p: ":code",
  scan: ":id",
};

/** `/r/*` → `r`, read from the table the app and the association files use. */
export const SHARE_PREFIXES: readonly string[] = SHARE_PATH_PATTERNS.map(
  (pattern) => pattern.replace(/^\/|\/\*$/g, ""),
);

/**
 * A share path: the prefix, then at least one character of segment.
 *
 * Case-insensitive and tolerant of doubled slashes, because a visitor's
 * address bar is both and the code is in the URL either way. `/r/` alone has
 * no code and is left as it is, and `/privacy` or `/press` never match: the
 * prefix must be followed by a slash.
 */
const SHARE_PATH = new RegExp(`^/+(${SHARE_PREFIXES.join("|")})/+[^/?#]`, "i");

/** Our own hosts. A referrer from anywhere else is never rewritten. */
const OWN_HOST = new URL(SHARE_ORIGIN).host;
const OWN_HOSTS = new Set([OWN_HOST, `www.${OWN_HOST}`]);

/**
 * A pathname with any share code replaced by its placeholder. Every other path
 * comes back unchanged. Anything BELOW a share prefix collapses to the
 * placeholder too, because the code is its first segment.
 */
export function shareSafePath(pathname: string): string {
  const prefix = SHARE_PATH.exec(pathname)?.[1]?.toLowerCase();
  if (!prefix) return pathname;
  return `/${prefix}/${SHARE_PLACEHOLDERS[prefix] ?? ":code"}`;
}

/** `?s=<surface>` when the search carries one we could have written, else "". */
function sourceQuery(search: string): string {
  const source = shareLinkSource(search);
  return source ? `?s=${source}` : "";
}

/**
 * A URL or a path with any share code taken out.
 *
 * - A path (`/r/AbCd?s=feed`) is always ours.
 * - An absolute URL is rewritten only when it is on this site — fazly.app,
 *   www.fazly.app, or `pageHost`, the host the page is being served from
 *   (staging, a preview, localhost). A REFERRER from elsewhere keeps its
 *   path: `https://www.reddit.com/r/<subreddit>` is a share-shaped path that
 *   says which community sent the visit, and has nothing of ours in it.
 * - Anything else — a slug, a label, a number as text — is returned as is.
 *
 * Never throws: an unparseable value is not a URL, so it is not one of ours.
 */
export function shareSafeUrl(value: string, pageHost?: string): string {
  try {
    if (value.startsWith("/") && !value.startsWith("//")) {
      const url = new URL(value, SHARE_ORIGIN);
      const safe = shareSafePath(url.pathname);
      return safe === url.pathname ? value : safe + sourceQuery(url.search);
    }
    if (!/^https?:\/\//i.test(value)) return value;
    const url = new URL(value);
    if (!OWN_HOSTS.has(url.host) && url.host !== pageHost) return value;
    const safe = shareSafePath(url.pathname);
    return safe === url.pathname
      ? value
      : `${url.origin}${safe}${sourceQuery(url.search)}`;
  } catch {
    return value;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Every URL and path inside an analytics payload, made share-safe — values AND
 * keys, at any depth.
 *
 * A sweep rather than a list of property names, and the list was the first
 * draft. posthog-js 1.414.0 writes the page's location under at least
 * `$current_url`, `$pathname`, `$referrer`, `$session_entry_url`,
 * `$session_entry_pathname`, `$session_entry_referrer`,
 * `$prev_pageview_pathname`, and — with person processing — `$initial_*`
 * copies of the first three (`@posthog/browser-common` event-utils,
 * `session-props.js`, `page-view.js`). It also writes it as a KEY: heatmap
 * events carry `$heatmap_data: { [href]: clicks }`, and heatmaps are switched
 * on from the PostHog dashboard, not from here. A named list is out of date
 * the first time a release adds a property; a sweep is not, and the real SDK
 * run in `analytics.test.ts` is what proves it covers what that version sends.
 *
 * Two keys that collapse into one keep both arrays (heatmap clicks from two
 * share landings in one flush); anything else keeps the later value.
 * Non-plain objects (a `Date` timestamp) are returned untouched.
 */
export function scrubShareCodes<T>(value: T, pageHost?: string): T {
  if (typeof value === "string") return shareSafeUrl(value, pageHost) as T;
  if (Array.isArray(value)) {
    return value.map((item) => scrubShareCodes(item, pageHost)) as T;
  }
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(value)) {
    const safeKey = shareSafeUrl(key, pageHost);
    const safeValue = scrubShareCodes(inner, pageHost);
    const existing = out[safeKey];
    out[safeKey] =
      safeKey !== key && Array.isArray(existing) && Array.isArray(safeValue)
        ? [...existing, ...safeValue]
        : safeValue;
  }
  return out as T;
}
